import type { AwsHostedSyncLayer } from './AwsHostedSyncLayer'

export const AWS_HOSTED_ACCOUNT_CONNECTOR_ID = 'aws-hosted-account'
export const AWS_HOSTED_ACCOUNT_SOURCE_KIND = 'aws-hosted-account'
export const AWS_HOSTED_SCANNER_VERSION = 'aws-hosted-sync.v1'
const AWS_HOSTED_EXTERNAL_ID_PREFIX = 'creately-aws'

export type AwsHostedAccountOrganizationMode =
  | 'single-account'
  | 'organization-member'
  | 'organization-management'

export type AwsHostedAccountCadence = 'manual' | 'daily' | 'weekly'

export type AwsHostedConnectionStatus =
  | 'pending_setup'
  | 'active'
  | 'test_failed'
  | 'disabled'

export interface AwsHostedAccountSourceMetadata {
  provider: 'aws'
  accountId: string | null
  roleArn: string
  externalIdRef: string
  externalIdPreview: string
  regions: string[]
  organizationMode: AwsHostedAccountOrganizationMode
  enabledLayers: AwsHostedSyncLayer[]
  cadence: AwsHostedAccountCadence
  status: AwsHostedConnectionStatus
  lastConnectionTestAt: string | null
  lastConnectionError?: string | null
  lastSuccessfulRunId?: string | null
  scannerVersion: string
}

export interface AwsHostedConnectInput {
  roleArn: string
  regions: string[]
  enabledLayers: AwsHostedSyncLayer[]
  cadence: AwsHostedAccountCadence
  externalId?: string | null
  displayName?: string | null
  organizationMode?: AwsHostedAccountOrganizationMode
}

export interface AwsHostedConnectionTestResult {
  ok: boolean
  accountId: string | null
  arn: string | null
  userId: string | null
  error?: string
}

export interface AwsHostedConnectionResult {
  sourceId: string
  sourceKey: string
  displayName: string | null
  metadata: AwsHostedAccountSourceMetadata
  connectionTest: AwsHostedConnectionTestResult
}

export function createAwsHostedSourceKey(roleArn: string): string {
  return `${AWS_HOSTED_ACCOUNT_CONNECTOR_ID}:${roleArn.trim().toLowerCase()}`
}

export function createAwsHostedExternalId(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  const token = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${AWS_HOSTED_EXTERNAL_ID_PREFIX}-${token}`
}

export function isAwsHostedAccountSourceMetadata(
  value: unknown,
): value is AwsHostedAccountSourceMetadata {
  if (!value || typeof value !== 'object') return false
  const meta = value as Partial<AwsHostedAccountSourceMetadata>
  return meta.provider === 'aws' &&
    typeof meta.roleArn === 'string' &&
    Array.isArray(meta.regions) &&
    Array.isArray(meta.enabledLayers)
}
